import type { HTMLAttributes, ReactElement } from "react";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import styles from "./Spinner.module.css";

export type SpinnerSize = "md" | "lg";

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  /**
   * What is loading, in plain language ("Loading users…"). Announced politely
   * to screen readers; required so a busy state is never silent.
   */
  label: string;
  /** Also show the label on screen, beside the indicator. */
  showLabel?: boolean;
}

/**
 * The busy indicator. Wrapped in a polite live region so assistive technology
 * announces the wait rather than reporting an empty page.
 *
 * The rotation animates `transform` only (compositor-only, never layout), and
 * the global `prefers-reduced-motion` rule stills it — the text label carries
 * the meaning, so a stilled spinner loses nothing.
 */
export function Spinner({
  size = "md",
  label,
  showLabel = false,
  className,
  ...rest
}: SpinnerProps): ReactElement {
  const classes = [styles.spinner, styles[size], className].filter(Boolean).join(" ");
  return (
    <div className={classes} role="status" aria-live="polite" {...rest}>
      <span className={styles.indicator} aria-hidden="true" />
      {showLabel ? <span className={styles.label}>{label}</span> : <VisuallyHidden>{label}</VisuallyHidden>}
    </div>
  );
}
